// contexts/CompanyContext.js
import { createContext, useContext, useEffect, useState } from 'react'
import { useAuth } from './AuthContext'
import {
  getFirestore,
  doc,
  getDoc,
  onSnapshot
} from 'firebase/firestore'
import { getStorage, ref, getDownloadURL } from 'firebase/storage'
import { app } from '../firebase'

const CompanyContext = createContext()

export function useCompany () {
  const context = useContext(CompanyContext)
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider')
  }
  return context
}

export function CompanyProvider ({ children }) {
  const { user } = useAuth()
  const [companySettings, setCompanySettings] = useState({
    botName: 'Service-Assistent', // Default value
    headerImageUrl: null
  })
  const [logoUrl, setLogoUrl] = useState(null)
  const [features, setFeatures] = useState({})
  const [companyStatus, setCompanyStatus] = useState('active')
  const [companyName, setCompanyName] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const db = getFirestore(app)

  // Load logo from Firebase Storage when logo feature is enabled
  useEffect(() => {
    const loadLogo = async () => {
      // Check if logo feature is enabled and user is logged in
      if (!features?.logo) {
        console.log('CompanyContext: Logo feature not enabled', { features })
        setLogoUrl(null)
        return
      }

      if (!user?.userData?.companyId) {
        console.log('CompanyContext: No companyId available', { user: user?.userData })
        setLogoUrl(null)
        return
      }

      try {
        const companyId = user.userData.companyId
        const storage = getStorage(app)
        
        // Try common image formats
        const imageFormats = ['logo.jpg', 'logo.png', 'logo.webp', 'logo.jpeg']
        
        for (const format of imageFormats) {
          try {
            const storagePath = `company-logos/${companyId}/${format}`
            const storageRef = ref(storage, storagePath)
            const url = await getDownloadURL(storageRef)
            console.log('CompanyContext: Logo loaded successfully', format, url)
            setLogoUrl(url)
            return
          } catch (err) {
            // Continue to next format if this one doesn't exist
            // Silently ignore 404 errors (object-not-found) as we're trying multiple formats
            if (err.code === 'storage/object-not-found') {
              // This is expected - just try the next format
              continue
            }
            // Only log actual errors (not 404s)
            if (err.code !== 'storage/unauthorized') {
              console.error('CompanyContext: Error loading logo format', format, err)
            }
            // If it's an authorization error, don't try other formats
            if (err.code === 'storage/unauthorized') {
              console.warn('CompanyContext: Unauthorized to access logo - check storage rules')
              break
            }
          }
        }

        // No logo found in any format
        console.log('CompanyContext: No logo found in any format for company', companyId)
        setLogoUrl(null)
      } catch (err) {
        console.error('CompanyContext: Error loading logo', err)
        setLogoUrl(null)
      }
    }

    loadLogo()
  }, [features?.logo, user?.userData?.companyId])

  useEffect(() => {
    if (!user || !user.userData?.companyId) {
      setLoading(false)
      return
    }

    const companyId = user.userData.companyId

    // Real-time listener for company settings
    const companyRef = doc(db, 'companies', companyId)

    const unsubscribe = onSnapshot(
      companyRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const companyData = docSnapshot.data()
          setCompanySettings({
            botName: companyData.settings?.botName || 'Service-Assistent',
            headerImageUrl: companyData.settings?.headerImageUrl || null
          })
          setFeatures(companyData.features || {})
          setCompanyStatus(companyData.status || 'active')
          setCompanyName(companyData.name || '')
        } else {
          setError('Company not found')
        }
        setLoading(false)
      },
      (err) => {
        console.error('Error loading company settings:', err)
        setError('Failed to load company settings')
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [db, user])

  const isCompanyActive = companyStatus === 'active'

  const value = {
    companySettings,
    features,
    companyStatus,
    companyName,
    isCompanyActive,
    loading,
    error,
    botName: companySettings.botName,
    headerImageUrl: companySettings.headerImageUrl,
    logoUrl
  }

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  )
}
